import axios from "axios";
import { API, getSessionId } from "@/lib/api";

// Compresse une image (blob/file) en JPEG, côté client.
export function compressImage(fileOrBlob, maxSize = 1600, quality = 0.8) {
    return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(fileOrBlob);
        img.onload = () => {
            let { width, height } = img;
            if (width > maxSize || height > maxSize) {
                const r = Math.min(maxSize / width, maxSize / height);
                width = Math.round(width * r);
                height = Math.round(height * r);
            }
            const c = document.createElement("canvas");
            c.width = width;
            c.height = height;
            c.getContext("2d").drawImage(img, 0, 0, width, height);
            c.toBlob((b) => {
                URL.revokeObjectURL(url);
                resolve(b);
            }, "image/jpeg", quality);
        };
        img.src = url;
    });
}

export async function uploadPhoto(blob, kind, measures) {
    const sid = getSessionId();
    const form = new FormData();
    form.append("file", blob, `${kind}.jpg`);
    form.append("kind", kind);
    if (measures) form.append("measures", JSON.stringify(measures));
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
            const { data } = await axios.post(`${API}/session/${sid}/photo`, form, { timeout: 45000 });
            return data;
        } catch (e) {
            lastErr = e;
            await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        }
    }
    throw lastErr;
}

export async function setEmail(email) {
    const sid = getSessionId();
    const { data } = await axios.post(`${API}/session/${sid}/email`, { email });
    return data;
}

export async function analyzeVision() {
    const sid = getSessionId();
    const { data } = await axios.post(`${API}/session/${sid}/analyze-vision`);
    return data;
}

export async function startVision() {
    const sid = getSessionId();
    if (!sid) return { status: "no_session" };
    const { data } = await axios.post(`${API}/session/${sid}/analyze-vision/start`);
    return data;
}

export async function visionStatus() {
    const sid = getSessionId();
    if (!sid) return { status: "no_session" };
    const { data } = await axios.get(`${API}/session/${sid}/analyze-vision/status`);
    return data;
}

export async function previewStart() {
    const sid = getSessionId();
    if (!sid) return { status: "no_session" };
    const { data } = await axios.post(`${API}/session/${sid}/vision/preview-start`);
    return data;
}

export async function previewStatus() {
    const sid = getSessionId();
    if (!sid) return { status: "no_session" };
    const { data } = await axios.get(`${API}/session/${sid}/vision/preview-status`);
    return data;
}

// Mesures faciales simples à partir des landmarks MediaPipe (normalisés 0-1).
export function computeFaceMeasures(landmarks) {
    const g = (i) => landmarks[i];
    const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, (a.z || 0) - (b.z || 0));
    try {
        const left = g(234), right = g(454), top = g(10), bottom = g(152);
        const width = dist(left, right);
        const height = dist(top, bottom);
        // symétrie : compare distances œil-nez gauche/droite
        const nose = g(1);
        const lEye = g(33), rEye = g(263);
        const dl = dist(lEye, nose), dr = dist(rEye, nose);
        const symetrie = Math.round((1 - Math.abs(dl - dr) / Math.max(dl, dr)) * 100);
        // tiers du visage
        const brow = g(9), noseBase = g(2);
        const t1 = dist(top, brow), t2 = dist(brow, noseBase), t3 = dist(noseBase, bottom);
        const sum = t1 + t2 + t3 || 1;
        const tiers = [t1 / sum, t2 / sum, t3 / sum].map((v) => Math.round(v * 100) / 100);
        // angle mâchoire
        const jl = g(172), chin = g(152), jr = g(397);
        const v1 = { x: jl.x - chin.x, y: jl.y - chin.y };
        const v2 = { x: jr.x - chin.x, y: jr.y - chin.y };
        const ang = Math.acos((v1.x * v2.x + v1.y * v2.y) /
            (Math.hypot(v1.x, v1.y) * Math.hypot(v2.x, v2.y) || 1));
        return {
            symetrie,
            ratio_largeur_hauteur: Math.round((width / (height || 1)) * 100) / 100,
            tiers_visage: tiers,
            angle_machoire: Math.round((ang * 180) / Math.PI),
        };
    } catch (e) {
        return {};
    }
}
